import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  Tab,
  Chip,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import RefreshIcon from "@material-ui/icons/Refresh";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import HistoryIcon from "@material-ui/icons/History";
import CheckIcon from "@material-ui/icons/Check";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import PersonIcon from "@material-ui/icons/Person";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import ViewListIcon from "@material-ui/icons/ViewList";
import SchoolIcon from "@material-ui/icons/School";
import GroupIcon from "@material-ui/icons/Group";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import AssignmentIcon from "@material-ui/icons/Assignment";
import FilterListIcon from "@material-ui/icons/FilterList";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExtensionIcon from "@material-ui/icons/Extension";
import BuildIcon from "@material-ui/icons/Build";
import SpaIcon from "@material-ui/icons/Spa";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import CategoryIcon from "@material-ui/icons/Category";
import ToysIcon from "@material-ui/icons/Toys";
import PublishIcon from "@material-ui/icons/Publish";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import StudentService from "./Locality/Service/studentService";
import SuperActivityService from "./Locality/Service/superActivityService";
import ActivityTabelService from "./Locality/Service/activityTabelService";
import ActivityService from "./Locality/Service/activityService";
import ImagesStoreService from "./Locality/Service/imagesStoreService";

const KEY_SEQUENCE = ["P", "WA", "WI", "M"];
const KEY_CIRCLE_COLORS = { P: "#1a9b54", WA: "#1769d6", WI: "#7c4dff", M: "#d4a017" };
const KEY_INCOMPLETE_COLOR = "#c5cdd8";
const KEY_INCOMPLETE_LABEL = "#b8c2cf";

const KEY_STEP_META = {
  P: { shortLabel: "Present", Icon: CheckIcon },
  WA: { shortLabel: "Work with Assist", Icon: PeopleOutlineIcon },
  WI: { shortLabel: "Work Independent", Icon: PersonIcon },
  M: { shortLabel: "Master", Icon: StarBorderIcon },
};

const normalizeActivityKey = (key) => String(key ?? "").trim();

const getActivityKeyIndex = (key) => {
  const normalized = normalizeActivityKey(key);
  if (!normalized) return -1;
  return KEY_SEQUENCE.indexOf(normalized);
};

const getKeyStatusText = (key) => {
  switch (normalizeActivityKey(key)) {
    case "P":
      return "Presented";
    case "WA":
      return "Work with Assistance";
    case "WI":
      return "Work Independently";
    case "M":
      return "Mastered";
    default:
      return key || "Not set";
  }
};
const MAX_IMAGES = 10;
const IMAGES_STORE_ALL_KEY = "ALL";

const AVATAR_BG = ["#ede7f6", "#fff3e0", "#fce4ec", "#e0f7fa", "#f1f8e9", "#e8eaf6"];
const AVATAR_FG = ["#5e35b1", "#ef6c00", "#c2185b", "#00838f", "#558b2f", "#3949ab"];

const DEFAULT_EXERCISE_ICONS = [
  { Icon: ExtensionIcon, bg: "#f3e5f5", color: "#7b1fa2" },
  { Icon: BuildIcon, bg: "#fff3e0", color: "#ef6c00" },
  { Icon: SpaIcon, bg: "#e8f5e9", color: "#2e7d32" },
  { Icon: EmojiObjectsIcon, bg: "#e3f2fd", color: "#1565c0" },
  { Icon: CategoryIcon, bg: "#fce4ec", color: "#c2185b" },
  { Icon: ToysIcon, bg: "#fff8e1", color: "#f9a825" },
];

const studentInitialsFromName = (name) => {
  if (!name || name === "—") return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const avatarColorsFromName = (name) => {
  const hash = (name || "").split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const i = hash % AVATAR_BG.length;
  return { backgroundColor: AVATAR_BG[i], color: AVATAR_FG[i] };
};

const calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();
  if (now.getDate() < birthDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return null;
  return { years, months };
};

const formatStudentAge = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return "";
  return `${age.years}y : ${age.months}m`;
};

const formatHalfYearAge = (totalMonths) => {
  const years = totalMonths / 12;
  return Number.isInteger(years) ? String(years) : years.toFixed(1);
};

// Half-year buckets: "2.5 to 3", "3 to 3.5", "3.5 to 4", ... (same as StudentActivity)
const getAgeGroupLabel = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return "Age N/A";
  const totalMonths = age.years * 12 + age.months;
  const bucketStart = Math.floor(totalMonths / 6) * 6;
  const bucketEnd = bucketStart + 6;
  return `${formatHalfYearAge(bucketStart)} to ${formatHalfYearAge(bucketEnd)}`;
};

const getAgeGroupSortKey = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return Infinity;
  return Math.floor((age.years * 12 + age.months) / 6) * 6;
};

const buildAgeWiseSummary = (students) => {
  const groups = new Map();
  students.forEach((student) => {
    const label = student.ageGroupLabel || getAgeGroupLabel(student.dob);
    if (!groups.has(label)) {
      groups.set(label, {
        sortKey: getAgeGroupSortKey(student.dob),
        count: 0,
      });
    }
    groups.get(label).count += 1;
  });
  return Array.from(groups.entries())
    .sort(([, a], [, b]) => a.sortKey - b.sortKey)
    .map(([label, group]) => [label, group.count]);
};

const getDefaultExerciseIcon = (index) =>
  DEFAULT_EXERCISE_ICONS[index % DEFAULT_EXERCISE_ICONS.length];

const resolveSuperActivityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return normalizeId(value._id || value.id);
  return normalizeId(value);
};

const getEffectiveSectionId = (requestedSectionId, sections) => {
  if (!requestedSectionId) return "";
  if (!Array.isArray(sections) || sections.length === 0) return "";
  const normalized = String(requestedSectionId);
  return sections.some((s) => String(s._id) === normalized) ? normalized : "";
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const recordMapKey = (activityId, subActivityId) =>
  `${normalizeId(activityId)}|${subActivityId ? normalizeId(subActivityId) : ""}`;

const studentRecordKey = (studentId, activityId, subActivityId) =>
  `${normalizeId(studentId)}|${recordMapKey(activityId, subActivityId)}`;

const getKeyIndex = (key) => getActivityKeyIndex(key);

const getUserContext = () => {
  const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const schooleId =
    userDetails?.schooleId?._id ||
    userDetails?.schooleId ||
    userDetails?.schoolId?._id ||
    userDetails?.schoolId ||
    "";
  return { userDetails, schooleId };
};

const getAcademicYear = () => {
  const y = new Date().getFullYear();
  return `${y}-${y + 1}`;
};

const getTodayIso = () => new Date().toISOString().split("T")[0];

const isRecordPublished = (record) =>
  String(record?.publicStatus || "").toLowerCase() === "publish";

const formatUpdatedDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 100px)",
    minHeight: 480,
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      height: "auto",
      minHeight: "calc(100vh - 80px)",
    },
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 4,
  },
  pageTitle: { fontSize: "1.35rem", fontWeight: 700, color: "#0f2d4d" },
  detailsViewBtn: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
    borderColor: "#1769d6",
    color: "#1769d6",
  },
  topBar: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr)) auto",
    alignItems: "flex-end",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(15,45,77,0.04)",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(1.25, 1.5),
    },
  },
  topLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#9aa6b6",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 6,
  },
  filterFormControl: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      background: "#fafbfc",
      minHeight: 40,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e6ef",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c5cdd8",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1769d6",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      paddingTop: 9,
      paddingBottom: 9,
      minHeight: "auto",
    },
  },
  filterSelectIconWrap: {
    marginLeft: 2,
    marginRight: 4,
    "& .MuiSvgIcon-root": {
      fontSize: 18,
      color: "#1769d6",
    },
  },
  filterSelectValue: {
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "#0f2d4d",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.3,
  },
  refreshBtn: {
    alignSelf: "flex-end",
    marginBottom: 2,
    color: "#1769d6",
    background: "#eef4ff",
    borderRadius: 10,
    padding: 8,
    "&:hover": { background: "#e0ecff" },
  },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: { flexDirection: "column" },
  },
  sidePanel: {
    width: 280,
    flexShrink: 0,
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(15,45,77,0.04)",
    [theme.breakpoints.down("sm")]: { width: "100%", maxHeight: 280 },
  },
  sidePanelHidden: {
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  sideTabs: {
    borderBottom: "1px solid #eef1f5",
    minHeight: 44,
    "& .MuiTab-root": {
      minHeight: 44,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.88rem",
    },
    "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
  },
  sideSearchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1.25, 1.5),
    borderBottom: "1px solid #eef1f5",
  },
  sideSearch: { flex: 1 },
  sideFilterBtn: {
    border: "1px solid #e0e6ef",
    borderRadius: 8,
    padding: 7,
    color: "#46566b",
  },
  sideList: { overflowY: "auto", flex: 1 },
  sideItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(1.25, 1.5),
    cursor: "pointer",
    borderBottom: "1px solid #f4f6fa",
    transition: "background 0.15s ease",
    "&:hover": { background: "#f8fafc" },
  },
  sideItemActive: {
    background: "#eef4ff",
    "& $sideItemName": { color: "#1769d6", fontWeight: 700 },
  },
  sideAvatar: {
    width: 40,
    height: 40,
    fontSize: "0.82rem",
    fontWeight: 700,
    flexShrink: 0,
  },
  sideItemName: {
    fontSize: "0.82rem",
    color: "#46566b",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    lineHeight: 1.3,
  },
  sideItemSub: {
    fontSize: "0.7rem",
    color: "#9aa6b6",
    marginTop: 2,
    textTransform: "none",
  },
  mobileStudentBar: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 12,
      border: "1px solid #e6eaf0",
      background: "#fff",
      cursor: "pointer",
    },
  },
  mainPanel: {
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(15,45,77,0.04)",
  },
  mainHeader: {
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #eef1f5",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1.5) },
  },
  mainHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
    flex: 1,
  },
  mainAvatar: {
    width: 52,
    height: 52,
    fontSize: "1rem",
    fontWeight: 700,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: { width: 44, height: 44, fontSize: "0.9rem" },
  },
  mainStudentName: {
    fontWeight: 700,
    color: "#0f2d4d",
    fontSize: "1.15rem",
    lineHeight: 1.25,
    [theme.breakpoints.down("xs")]: { fontSize: "1rem" },
  },
  breadcrumb: {
    fontSize: "0.8rem",
    color: "#9aa6b6",
    marginTop: 4,
    "& strong": { color: "#46566b", fontWeight: 600 },
  },
  mainHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  centerSearch: {
    minWidth: 200,
    [theme.breakpoints.down("xs")]: { minWidth: 0, flex: 1 },
    "& .MuiOutlinedInput-root": { borderRadius: 10, background: "#fafbfc" },
  },
  activityList: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5, 2),
    background: "#f8f9fb",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
  },
  exerciseCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 1.75),
    marginBottom: theme.spacing(1.25),
    borderRadius: 12,
    border: "1px solid #e8ecf2",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15,45,77,0.03)",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      padding: theme.spacing(1.25),
    },
  },
  exerciseIconBox: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: { width: 42, height: 42, borderRadius: 10 },
  },
  exerciseInfo: {
    flex: 1,
    minWidth: 140,
    [theme.breakpoints.down("xs")]: { minWidth: 0, width: "calc(100% - 58px)" },
  },
  exerciseTitle: {
    fontWeight: 700,
    color: "#0f2d4d",
    fontSize: "0.92rem",
    lineHeight: 1.3,
  },
  exerciseDesc: {
    fontSize: "0.78rem",
    color: "#9aa6b6",
    marginTop: 3,
    lineHeight: 1.4,
  },
  exerciseProgress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "1 1 auto",
    minWidth: 200,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start",
      paddingTop: 4,
      borderTop: "1px solid #f0f2f6",
      marginTop: 4,
    },
  },
  exerciseActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      marginLeft: "auto",
    },
  },
  addRecordBtn: {
    background: "#1769d6",
    color: "#fff",
    width: 34,
    height: 34,
    borderRadius: 10,
    "&:hover": { background: "#125bbd" },
  },
  statusDraft: {
    backgroundColor: "#e8f0fe",
    color: "#1769d6",
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
  },
  statusPublished: {
    backgroundColor: "#e6f7ed",
    color: "#1a9b54",
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
  },
  emptyState: { padding: theme.spacing(6, 2), textAlign: "center", color: "#9aa6b6" },
  detailDrawer: { width: "100%", maxWidth: 520 },
  detailContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    height: "100%",
  },
  imageThumb: {
    width: 72,
    height: 72,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #e6eaf0",
  },
  keyStepRow: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 2,
    flexWrap: "wrap",
  },
  keyStepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    minWidth: 56,
    padding: "4px 2px",
    borderRadius: 8,
    userSelect: "none",
  },
  keyStepItemCompact: {
    minWidth: 48,
    padding: "2px 1px",
  },
  keyStepItemDoneP: {
    backgroundColor: "#e6f7ed",
  },
  keyStepIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: "50%",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  keyStepIconWrapCompact: {
    width: 26,
    height: 26,
  },
  keyStepLabel: {
    fontSize: "0.58rem",
    fontWeight: 600,
    lineHeight: 1.15,
    textAlign: "center",
    maxWidth: 62,
  },
  keyStepLabelCompact: {
    fontSize: "0.52rem",
    maxWidth: 52,
  },
  keyStepClickable: {
    cursor: "pointer",
    "&:hover $keyStepIconWrap": {
      boxShadow: "0 0 0 2px rgba(23,105,214,0.25)",
    },
  },
  keyStepDisabled: {
    cursor: "default",
  },
  progressCell: {
    overflowX: "auto",
    maxWidth: "100%",
  },
  storeImage: {
    width: 64,
    height: 64,
    objectFit: "cover",
    borderRadius: 8,
    cursor: "pointer",
    border: "2px solid transparent",
  },
  storeImageSelected: { border: "2px solid #1769d6" },
  studentDrawerPaper: {
    width: "100%",
    maxWidth: 320,
  },
}));

function ActivityKeyStepper({ currentKey, onSelect, updating, compact = false }) {
  const classes = useStyles();
  const normalizedKey = normalizeActivityKey(currentKey);
  const currentIndex = getActivityKeyIndex(currentKey);

  if (updating) {
    return <CircularProgress size={compact ? 14 : 18} />;
  }

  return (
    <Box className={classes.keyStepRow}>
      {KEY_SEQUENCE.map((key) => {
        const stepIndex = KEY_SEQUENCE.indexOf(key);
        const color = KEY_CIRCLE_COLORS[key];
        const meta = KEY_STEP_META[key];
        const StepIcon = meta.Icon;
        const isCurrent = Boolean(normalizedKey) && key === normalizedKey;
        const isDone = currentIndex >= 0 && stepIndex < currentIndex;
        const isComplete = isDone || isCurrent;
        const isClickable = !isCurrent;

        const iconWrapStyle = {
          border: `2px solid ${KEY_INCOMPLETE_COLOR}`,
          backgroundColor: "#fff",
          color: KEY_INCOMPLETE_COLOR,
        };
        let labelColor = KEY_INCOMPLETE_LABEL;

        if (isComplete) {
          labelColor = color;
          if (isDone) {
            iconWrapStyle.backgroundColor = color;
            iconWrapStyle.border = `2px solid ${color}`;
            iconWrapStyle.color = "#fff";
          } else {
            iconWrapStyle.backgroundColor = "#fff";
            iconWrapStyle.border = `2px solid ${color}`;
            iconWrapStyle.color = color;
          }
        }

        const itemClassName = [
          classes.keyStepItem,
          compact && classes.keyStepItemCompact,
          isDone && key === "P" && classes.keyStepItemDoneP,
          isClickable && classes.keyStepClickable,
          !isClickable && classes.keyStepDisabled,
        ]
          .filter(Boolean)
          .join(" ");

        const tooltipTitle = isClickable
          ? isDone
            ? `${meta.shortLabel} — click to change back`
            : `${meta.shortLabel} — click to update`
          : isCurrent
            ? `${meta.shortLabel} (current)`
            : isDone
              ? `${meta.shortLabel} (completed)`
              : meta.shortLabel;

        const handleStepClick = (e) => {
          if (!isClickable) return;
          e.stopPropagation();
          onSelect(key);
        };

        return (
          <Tooltip key={key} title={tooltipTitle} arrow>
            <Box
              className={itemClassName}
              onClick={handleStepClick}
              onKeyDown={(e) => {
                if (isClickable && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onSelect(key);
                }
              }}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : -1}
              aria-label={tooltipTitle}
            >
              <span
                className={`${classes.keyStepIconWrap} ${
                  compact ? classes.keyStepIconWrapCompact : ""
                }`}
                style={iconWrapStyle}
              >
                <StepIcon style={{ fontSize: compact ? 14 : isDone && key === "P" ? 16 : 15 }} />
              </span>
              <Typography
                component="span"
                className={`${classes.keyStepLabel} ${
                  compact ? classes.keyStepLabelCompact : ""
                }`}
                style={{ color: labelColor }}
              >
                {meta.shortLabel}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default function StudentActivityQuick() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [allActivities, setAllActivities] = useState([]);

  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [selectedAgeFilter, setSelectedAgeFilter] = useState("");
  const [superActivityId, setSuperActivityId] = useState("");
  const [activityId, setActivityId] = useState("");
  const [sideTab, setSideTab] = useState("students");
  const [sideSearch, setSideSearch] = useState("");
  const [centerSearch, setCenterSearch] = useState("");
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [mobileStudentDrawerOpen, setMobileStudentDrawerOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState({
    anchor: null,
    activity: null,
    subActivity: null,
    targetStudentId: null,
    record: null,
  });

  const [studentRecordsMap, setStudentRecordsMap] = useState({});
  const [workRecordsMap, setWorkRecordsMap] = useState({});

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [updatingKey, setUpdatingKey] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState("");

  const [snack, setSnack] = useState({ open: false, message: "" });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContext, setDetailContext] = useState(null);
  const [observationText, setObservationText] = useState("");
  const [isListeningObservation, setIsListeningObservation] = useState(false);
  const speechRecognitionRef = useRef(null);
  const observationTextRef = useRef("");
  const [savingDetail, setSavingDetail] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [keyConfirm, setKeyConfirm] = useState({
    open: false,
    activity: null,
    subActivity: null,
    targetStudentId: null,
    currentKey: "",
    nextKey: "",
  });
  const [statusConfirmDialog, setStatusConfirmDialog] = useState({
    open: false,
    record: null,
    publicStatus: "",
  });
  const [addRecordDialog, setAddRecordDialog] = useState({
    open: false,
    activity: null,
    subActivity: null,
    targetStudentId: null,
    key: "",
    startDate: "",
    endDate: "",
    saving: false,
  });
  const [imagesStore, setImagesStore] = useState({
    open: false,
    loading: false,
    rows: [],
    selectedUrls: [],
    selectedStoreId: IMAGES_STORE_ALL_KEY,
  });

  const selectedStudent = useMemo(
    () => studentList.find((s) => s._id === studentId),
    [studentList, studentId]
  );

  const filteredAreas = useMemo(() => {
    if (!classId) return [];
    return areaList.filter((a) => normalizeId(a.classId) === normalizeId(classId));
  }, [areaList, classId]);

  const classActivities = useMemo(() => {
    if (!classId) return [];
    return allActivities.filter(
      (a) => normalizeId(a.classId) === normalizeId(classId)
    );
  }, [allActivities, classId]);

  const areaActivities = useMemo(() => {
    if (!superActivityId) return classActivities;
    return classActivities.filter(
      (a) => resolveSuperActivityId(a.superActivityId) === normalizeId(superActivityId)
    );
  }, [classActivities, superActivityId]);

  const selectedActivity = useMemo(
    () =>
      areaActivities.find((a) => String(a._id) === String(activityId)) ||
      classActivities.find((a) => String(a._id) === String(activityId)) ||
      null,
    [areaActivities, classActivities, activityId]
  );

  const flatExercises = useMemo(() => {
    if (!activityId) return [];
    const activity =
      areaActivities.find((a) => String(a._id) === String(activityId)) ||
      classActivities.find((a) => String(a._id) === String(activityId));
    if (!activity) return [];

    const subs = Array.isArray(activity.subActivities) ? activity.subActivities : [];
    if (subs.length) {
      return subs.map((sub) => ({
        id: recordMapKey(activity._id, sub._id),
        activity,
        subActivity: sub,
        label: sub.subActivityName,
        parentLabel: activity.activityName,
      }));
    }

    return [{
      id: recordMapKey(activity._id, null),
      activity,
      subActivity: null,
      label: activity.activityName,
      parentLabel: null,
    }];
  }, [areaActivities, classActivities, activityId]);

  const filteredSideStudents = useMemo(() => {
    let list = studentList;
    if (selectedAgeFilter) {
      list = list.filter((s) => s.ageGroupLabel === selectedAgeFilter);
    }
    const q = sideSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => s.name.toLowerCase().includes(q));
  }, [studentList, sideSearch, selectedAgeFilter]);

  const studentAgeWiseSummary = useMemo(() => buildAgeWiseSummary(studentList), [studentList]);

  const handleAgeChipSelect = (ageLabel) => {
    if (selectedAgeFilter === ageLabel) {
      setSelectedAgeFilter("");
      return;
    }
    setSelectedAgeFilter(ageLabel);
    const firstInAge = studentList.find((s) => s.ageGroupLabel === ageLabel);
    if (firstInAge) setStudentId(firstInAge._id);
  };

  const handleSelectAllAgeChip = () => {
    setSelectedAgeFilter("");
  };

  const filteredSideExercises = useMemo(() => {
    const q = sideSearch.trim().toLowerCase();
    if (!q) return flatExercises;
    return flatExercises.filter(
      (ex) =>
        ex.label.toLowerCase().includes(q) ||
        (ex.parentLabel && ex.parentLabel.toLowerCase().includes(q))
    );
  }, [flatExercises, sideSearch]);

  const showSnack = (message) => setSnack({ open: true, message });

  const loadSections = useCallback((nextClassId, defaultSectionId) => {
    const { schooleId, userDetails } = getUserContext();
    if (!schooleId || !nextClassId) {
      setSectionList([]);
      return Promise.resolve();
    }
    return SectionService.findSectionList({ schooleId, classId: nextClassId })
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setSectionList(list);

        if (!list.length) {
          setSectionId("");
          return { sectionId: "", sections: list };
        }

        const normalizedDefault = defaultSectionId ? String(defaultSectionId) : "";
        const teacherPrimaryClass = normalizeId(userDetails.classId);
        const teacherPrimarySection = userDetails.sectionId
          ? String(normalizeId(userDetails.sectionId))
          : "";
        const canUseDefault =
          normalizedDefault &&
          list.some((s) => String(s._id) === normalizedDefault);
        const canUseTeacherSection =
          !normalizedDefault &&
          normalizeId(nextClassId) === teacherPrimaryClass &&
          teacherPrimarySection &&
          list.some((s) => String(s._id) === teacherPrimarySection);

        let nextSectionId = "";
        if (canUseDefault) nextSectionId = normalizedDefault;
        else if (canUseTeacherSection) nextSectionId = teacherPrimarySection;
        else if (list.length === 1) nextSectionId = String(list[0]._id);

        setSectionId(nextSectionId);
        return { sectionId: nextSectionId, sections: list };
      })
      .catch(() => {
        setSectionList([]);
        setSectionId("");
        return { sectionId: "", sections: [] };
      });
  }, []);

  const loadStudents = useCallback((nextClassId, nextSectionId, preserveStudentId, sectionsForClass) => {
    const { schooleId } = getUserContext();
    if (!schooleId || !nextClassId) {
      setStudentList([]);
      return Promise.resolve();
    }
    setLoadingStudents(true);
    const body = { classId: nextClassId };
    const sections = sectionsForClass ?? sectionList;
    const effectiveSectionId = getEffectiveSectionId(nextSectionId, sections);
    if (effectiveSectionId) body.sectionId = effectiveSectionId;

    return StudentService.getAllStudentById(schooleId, body)
      .then((res) => {
        const list = (Array.isArray(res) ? res : []).map((s) => ({
          _id: s._id,
          name: `${s.firstName || ""} ${s.lastName || ""}`.trim(),
          dob: s.dob,
          ageLabel: formatStudentAge(s.dob),
          ageGroupLabel: getAgeGroupLabel(s.dob),
        }));
        setStudentList(list);
        setSelectedAgeFilter("");
        const keepId = preserveStudentId || studentId;
        if (keepId && list.find((s) => s._id === keepId)) setStudentId(keepId);
        else setStudentId(list[0]?._id || "");
      })
      .catch(() => setStudentList([]))
      .finally(() => setLoadingStudents(false));
  }, [studentId, sectionList]);

  const loadAreasAndActivities = useCallback((nextClassId, resetArea = false) => {
    const { schooleId } = getUserContext();
    const normalizedClassId = normalizeId(nextClassId);
    if (!schooleId || !normalizedClassId) {
      setAreaList([]);
      setAllActivities([]);
      return Promise.resolve();
    }
    setLoadingActivities(true);
    return Promise.all([
      SuperActivityService.getAllSuperActivityById(schooleId, { classId: normalizedClassId }),
      ActivityTabelService.getActivitiesWithExercises({ schooleId, classId: normalizedClassId }),
    ])
      .then(([areas, activities]) => {
        const areaArr = Array.isArray(areas) ? areas : [];
        const actArr = Array.isArray(activities) ? activities : [];
        setAreaList(areaArr);
        setAllActivities(actArr);

        const classAreas = areaArr.filter(
          (a) => normalizeId(a.classId) === normalizedClassId
        );
        let nextSuperActivityId = classAreas[0]?._id
          ? String(classAreas[0]._id)
          : "";

        if (!nextSuperActivityId && actArr.length > 0) {
          nextSuperActivityId = resolveSuperActivityId(actArr[0].superActivityId);
        }

        const currentSuperValid =
          superActivityId &&
          (classAreas.some((a) => String(a._id) === String(superActivityId)) ||
            actArr.some(
              (a) => resolveSuperActivityId(a.superActivityId) === normalizeId(superActivityId)
            ));

        if (resetArea || !superActivityId || !currentSuperValid) {
          setSuperActivityId(nextSuperActivityId);
        }
      })
      .catch(() => {
        setAreaList([]);
        setAllActivities([]);
      })
      .finally(() => setLoadingActivities(false));
  }, [superActivityId]);

  const loadStudentModeRecords = useCallback(() => {
    const { schooleId } = getUserContext();
    if (!schooleId || !classId || !studentId) {
      setStudentRecordsMap({});
      return Promise.resolve([]);
    }
    setLoadingRecords(true);
    return ActivityService.getStudentActivityRecords({
      schooleId,
      classId,
      sectionId: sectionId || undefined,
      studentId,
      superActivityId: superActivityId || undefined,
      showMaster: true,
    })
      .then((res) => {
        const records = Array.isArray(res) ? res : [];
        const map = {};
        records.forEach((record) => {
          const key = recordMapKey(record.activityId, record.subActivityId);
          if (!map[key]) map[key] = record;
        });
        setStudentRecordsMap(map);
        return records;
      })
      .catch(() => {
        setStudentRecordsMap({});
        return [];
      })
      .finally(() => setLoadingRecords(false));
  }, [classId, sectionId, studentId, superActivityId]);

  const loadWorkModeRecords = useCallback(() => {
    const { schooleId } = getUserContext();
    if (!schooleId || !classId || !selectedExercise?.activity?._id) {
      setWorkRecordsMap({});
      return Promise.resolve([]);
    }
    setLoadingRecords(true);
    const body = {
      schooleId,
      classId,
      sectionId: sectionId || undefined,
      superActivityId: superActivityId || undefined,
      activityId: selectedExercise.activity._id,
      subActivityId: selectedExercise.subActivity?._id || null,
      showMaster: true,
    };
    return ActivityService.getStudentActivityRecords(body)
      .then((res) => {
        const records = Array.isArray(res) ? res : [];
        const map = {};
        records.forEach((record) => {
          const sid = normalizeId(record.studentId);
          if (sid && !map[sid]) map[sid] = record;
        });
        setWorkRecordsMap(map);
        return records;
      })
      .catch(() => {
        setWorkRecordsMap({});
        return [];
      })
      .finally(() => setLoadingRecords(false));
  }, [classId, sectionId, superActivityId, selectedExercise]);

  useEffect(() => {
    const { userDetails, schooleId } = getUserContext();
    if (!schooleId) return;
    AddClassService.getAllAddClass(schooleId).then((res) => {
      const list = Array.isArray(res) ? res : [];
      setClassList(list);
      const defaultClassId =
        normalizeId(userDetails.classId) ||
        (list[0]?._id ? String(list[0]._id) : "");
      if (defaultClassId) {
        const defaultSection = userDetails.sectionId
          ? String(normalizeId(userDetails.sectionId))
          : "";
        setClassId(defaultClassId);
        loadSections(defaultClassId, defaultSection).then(() => {
          loadAreasAndActivities(defaultClassId);
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!classId) return;
    const effectiveSectionId = getEffectiveSectionId(sectionId, sectionList);
    if (sectionId && sectionList.length > 0 && !effectiveSectionId) {
      setSectionId("");
      return;
    }
    loadStudents(classId, effectiveSectionId, studentId, sectionList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, sectionId, sectionList]);

  useEffect(() => {
    const source = superActivityId ? areaActivities : classActivities;
    if (!source.length) {
      setActivityId("");
      return;
    }
    if (!source.find((a) => String(a._id) === String(activityId))) {
      setActivityId(String(source[0]._id));
    }
  }, [areaActivities, classActivities, activityId, superActivityId]);

  useEffect(() => {
    if (!classId || superActivityId) return;
    if (filteredAreas.length > 0) {
      setSuperActivityId(String(filteredAreas[0]._id));
      return;
    }
    if (classActivities.length > 0) {
      const derived = resolveSuperActivityId(classActivities[0].superActivityId);
      if (derived) setSuperActivityId(derived);
    }
  }, [classId, classActivities, filteredAreas, superActivityId]);

  useEffect(() => {
    if (sideTab !== "works") return;
    if (!flatExercises.length) {
      setSelectedExercise(null);
      return;
    }
    if (!selectedExercise || !flatExercises.find((e) => e.id === selectedExercise.id)) {
      setSelectedExercise(flatExercises[0]);
    }
  }, [flatExercises, selectedExercise, sideTab]);

  useEffect(() => {
    if (sideTab === "students") loadStudentModeRecords();
  }, [sideTab, loadStudentModeRecords]);

  useEffect(() => {
    if (sideTab === "works") loadWorkModeRecords();
  }, [sideTab, loadWorkModeRecords]);

  const refreshRecords = () =>
    sideTab === "students" ? loadStudentModeRecords() : loadWorkModeRecords();

  const getStudentRecord = (activityId, subActivityId) =>
    studentRecordsMap[recordMapKey(activityId, subActivityId)] || null;

  const getWorkRecord = (targetStudentId) =>
    workRecordsMap[normalizeId(targetStudentId)] || null;

  const upsertStudentRecord = (record) => {
    const key = recordMapKey(record.activityId, record.subActivityId);
    setStudentRecordsMap((prev) => ({ ...prev, [key]: record }));
  };

  const upsertWorkRecord = (targetStudentId, record) => {
    setWorkRecordsMap((prev) => ({
      ...prev,
      [normalizeId(targetStudentId)]: record,
    }));
  };

  const patchRecordPublicStatus = (recordId, publicStatus) => {
    setStudentRecordsMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (String(next[key]?._id) === String(recordId)) {
          next[key] = { ...next[key], publicStatus };
        }
      });
      return next;
    });
    setWorkRecordsMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (String(next[key]?._id) === String(recordId)) {
          next[key] = { ...next[key], publicStatus };
        }
      });
      return next;
    });
    setDetailContext((prev) =>
      prev?.record?._id === recordId
        ? { ...prev, record: { ...prev.record, publicStatus } }
        : prev
    );
  };

  const handlePublicStatusChange = (record, publicStatus) => {
    if (!record?._id) return;

    if (publicStatus === "publish" && getActivityKeyIndex(record?.key) === -1) {
      showSnack("Please set a key (P, WA, WI or M) before publishing.");
      return;
    }

    setStatusConfirmDialog({
      open: true,
      record,
      publicStatus,
    });
  };

  const closeStatusConfirmDialog = () => {
    setStatusConfirmDialog({ open: false, record: null, publicStatus: "" });
  };

  const confirmPublicStatusChange = async () => {
    const { record, publicStatus } = statusConfirmDialog;
    if (!record?._id || !publicStatus) {
      closeStatusConfirmDialog();
      return;
    }

    const isPublishing = publicStatus === "publish";
    closeStatusConfirmDialog();
    setUpdatingStatus(record._id);

    try {
      await ActivityService.upadePublicStatus({
        _id: record._id,
        publicStatus,
      });
      patchRecordPublicStatus(record._id, publicStatus);
      showSnack(
        isPublishing
          ? "Activity published to parents"
          : "Activity moved back to draft"
      );
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update publish status");
    } finally {
      setUpdatingStatus("");
    }
  };

  const renderPublishStatusChip = (record) => {
    if (!record?._id) return null;
    const isPublished = isRecordPublished(record);
    const busy = updatingStatus === record._id;
    return (
      <Chip
        size="small"
        label={busy ? "..." : isPublished ? "Published" : "Draft"}
        className={isPublished ? classes.statusPublished : classes.statusDraft}
        onClick={() =>
          !busy && handlePublicStatusChange(record, isPublished ? "draft" : "publish")
        }
        disabled={busy}
      />
    );
  };

  const getStudentNameById = (sid) =>
    studentList.find((s) => s._id === sid)?.name || "Student";

  const getStudentNameWithAgeById = (sid) => {
    const student = studentList.find((s) => s._id === sid);
    if (!student) return "Student";
    return student.ageLabel ? `${student.name} (${student.ageLabel})` : student.name;
  };

  const saveQuickActivityRecord = async ({
    activity,
    subActivity,
    targetStudentId,
    key,
    startDate,
    endDate,
  }) => {
    const { userDetails, schooleId } = getUserContext();
    const sid = targetStudentId || studentId;
    const response = await ActivityService.addQuickActivityRecord({
      schooleId,
      classId,
      sectionId: sectionId || null,
      studentId: sid,
      activityId: activity._id,
      superActivityId: normalizeId(activity.superActivityId) || superActivityId,
      subActivityId: subActivity?._id || null,
      key: normalizeActivityKey(key),
      date: startDate,
      endDate,
      sfd: startDate,
      remarks: "",
      isFuturePlanning: false,
      authorizedPerson: userDetails.email || "",
      academicYear: getAcademicYear(),
      teacherId: userDetails.teacherId || null,
    });
    const record = response?.activityRecord;
    if (record) {
      if (sideTab === "works") upsertWorkRecord(sid, record);
      else upsertStudentRecord(record);
    }
    await refreshRecords();
    return record;
  };

  const applyKeyUpdate = async (activity, subActivity, targetKey, targetStudentId) => {
    const sid = targetStudentId || studentId;
    const rowKey =
      sideTab === "works"
        ? studentRecordKey(sid, activity._id, subActivity?._id)
        : recordMapKey(activity._id, subActivity?._id);

    setUpdatingKey(rowKey);
    try {
      const existing =
        sideTab === "works"
          ? getWorkRecord(sid)
          : getStudentRecord(activity._id, subActivity?._id);

      if (existing) {
        const normalizedExistingKey = normalizeActivityKey(existing.key);
        const normalizedTargetKey = normalizeActivityKey(targetKey);
        if (normalizedExistingKey === normalizedTargetKey) return;
        const response = await ActivityService.updateActivityStatus({
          id: existing._id,
          key: normalizedTargetKey,
        });
        const updated = {
          ...existing,
          ...(response?.activityRecord || {}),
          key: normalizedTargetKey,
          updatedAt: new Date().toISOString(),
        };
        if (sideTab === "works") upsertWorkRecord(sid, updated);
        else upsertStudentRecord(updated);
        setDetailContext((prev) => {
          if (!prev?.record?._id || String(prev.record._id) !== String(updated._id)) {
            return prev;
          }
          return { ...prev, record: { ...prev.record, ...updated } };
        });
        showSnack("Progress updated");
      }
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update progress");
    } finally {
      setUpdatingKey("");
    }
  };

  const requestKeyUpdate = (activity, subActivity, targetKey, targetStudentId) => {
    const sid = targetStudentId || studentId;
    const existing =
      sideTab === "works"
        ? getWorkRecord(sid)
        : getStudentRecord(activity._id, subActivity?._id);
    const nextKey = normalizeActivityKey(targetKey);

    if (!existing) {
      const today = getTodayIso();
      setAddRecordDialog({
        open: true,
        activity,
        subActivity,
        targetStudentId: sid,
        key: nextKey,
        startDate: today,
        endDate: today,
        saving: false,
      });
      return;
    }

    const currentKey = normalizeActivityKey(existing?.key);
    if (currentKey === nextKey) return;
    setKeyConfirm({
      open: true,
      activity,
      subActivity,
      targetStudentId: sid,
      currentKey,
      nextKey,
    });
  };

  const confirmKeyUpdate = async () => {
    const { activity, subActivity, targetStudentId, nextKey } = keyConfirm;
    setKeyConfirm((p) => ({ ...p, open: false }));
    await applyKeyUpdate(activity, subActivity, nextKey, targetStudentId);
  };

  const closeAddRecordDialog = () => {
    setAddRecordDialog({
      open: false,
      activity: null,
      subActivity: null,
      targetStudentId: null,
      key: "",
      startDate: "",
      endDate: "",
      saving: false,
    });
  };

  const confirmAddRecord = async () => {
    const { activity, subActivity, targetStudentId, key, startDate, endDate } = addRecordDialog;
    if (!activity || !key || !startDate || !endDate) {
      showSnack("Start date and end date are required");
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      showSnack("End date cannot be before start date");
      return;
    }

    const rowKey =
      sideTab === "works"
        ? studentRecordKey(targetStudentId, activity._id, subActivity?._id)
        : recordMapKey(activity._id, subActivity?._id);

    setAddRecordDialog((p) => ({ ...p, saving: true }));
    setUpdatingKey(rowKey);
    try {
      await saveQuickActivityRecord({
        activity,
        subActivity,
        targetStudentId,
        key,
        startDate,
        endDate,
      });
      closeAddRecordDialog();
      showSnack("Activity recorded");
    } catch (err) {
      setAddRecordDialog((p) => ({ ...p, saving: false }));
      showSnack(err?.response?.data?.message || "Failed to add activity record");
    } finally {
      setUpdatingKey("");
    }
  };

  const stopObservationVoice = useCallback(() => {
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      try {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.stop();
      } catch (_) {
        /* ignore */
      }
      speechRecognitionRef.current = null;
    }
    setIsListeningObservation(false);
  }, []);

  const startObservationVoice = useCallback(() => {
    const SpeechRecognitionAPI =
      typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

    if (!SpeechRecognitionAPI) {
      showSnack("Voice input is not supported in this browser. Try Chrome.");
      return;
    }

    stopObservationVoice();

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-IN";

    recognition.onresult = (event) => {
      let finalChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) {
          finalChunk += transcript;
        }
      }
      if (!finalChunk.trim()) return;

      const base = observationTextRef.current || "";
      const needsSpace = base && !/\s$/.test(base);
      const next = `${base}${needsSpace ? " " : ""}${finalChunk.trim()}`;
      observationTextRef.current = next;
      setObservationText(next);
    };

    recognition.onerror = (event) => {
      const code = event?.error;
      if (code === "not-allowed" || code === "service-not-allowed") {
        showSnack("Microphone permission denied. Allow mic access and try again.");
      } else if (code !== "aborted" && code !== "no-speech") {
        showSnack("Voice input error. Please try again.");
      }
      stopObservationVoice();
    };

    recognition.onend = () => {
      // Keep listening until user stops (continuous mode can end after silence).
      if (speechRecognitionRef.current === recognition) {
        try {
          recognition.start();
        } catch (_) {
          setIsListeningObservation(false);
          speechRecognitionRef.current = null;
        }
      }
    };

    speechRecognitionRef.current = recognition;
    observationTextRef.current = observationTextRef.current || "";
    try {
      recognition.start();
      setIsListeningObservation(true);
      showSnack("Listening… speak your observation");
    } catch (_) {
      showSnack("Could not start voice input");
      stopObservationVoice();
    }
  }, [stopObservationVoice, showSnack]);

  const toggleObservationVoice = useCallback(() => {
    if (isListeningObservation) {
      stopObservationVoice();
      showSnack("Voice input stopped");
      return;
    }
    startObservationVoice();
  }, [isListeningObservation, startObservationVoice, stopObservationVoice, showSnack]);

  useEffect(() => {
    observationTextRef.current = observationText;
  }, [observationText]);

  useEffect(() => () => stopObservationVoice(), [stopObservationVoice]);

  const openDetail = (activity, subActivity, targetStudentId) => {
    const sid = targetStudentId || studentId;
    const record =
      sideTab === "works"
        ? getWorkRecord(sid)
        : getStudentRecord(activity._id, subActivity?._id);
    setDetailContext({ activity, subActivity, record, targetStudentId: sid });
    const remarks = record?.remarks || "";
    observationTextRef.current = remarks;
    setObservationText(remarks);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    stopObservationVoice();
    setDetailOpen(false);
    setDetailContext(null);
    observationTextRef.current = "";
    setObservationText("");
    setImagesStore({
      open: false,
      loading: false,
      rows: [],
      selectedUrls: [],
      selectedStoreId: IMAGES_STORE_ALL_KEY,
    });
  };

  const ensureRecordForDetail = async () => {
    if (detailContext?.record?._id) return detailContext.record;
    const { activity, subActivity, targetStudentId } = detailContext;
    const today = getTodayIso();
    const record = await saveQuickActivityRecord({
      activity,
      subActivity,
      targetStudentId,
      key: "P",
      startDate: today,
      endDate: today,
    });
    if (record) {
      setDetailContext((prev) => ({ ...prev, record }));
      return record;
    }
    throw new Error("Could not create activity record");
  };

  const saveObservation = async () => {
    if (!detailContext) return;
    setSavingDetail(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      await ActivityService.patchActivityRecord({
        _id: record._id,
        remarks: observationText.trim(),
      });
      const updated = { ...record, remarks: observationText.trim() };
      if (sideTab === "works") upsertWorkRecord(detailContext.targetStudentId, updated);
      else upsertStudentRecord(updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      showSnack("Observation saved");
      closeDetail();
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to save observation");
    } finally {
      setSavingDetail(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length || !detailContext) return;
    setUploadingImage(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      const existing = Array.isArray(record.imageURL) ? record.imageURL.filter(Boolean) : [];
      const remaining = MAX_IMAGES - existing.length;
      const toUpload = files.slice(0, remaining);
      const uploadResults = await Promise.all(
        toUpload.map((file) => {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("imageName", file.name);
          return StudentService.uploadImage(formData);
        })
      );
      const urls = uploadResults.map((r) => r?.url).filter(Boolean);
      if (!urls.length) throw new Error("Upload failed");
      const imageURL = [...existing, ...urls];
      await ActivityService.patchActivityRecord({ _id: record._id, imageURL });
      const updated = { ...record, imageURL };
      if (sideTab === "works") upsertWorkRecord(detailContext.targetStudentId, updated);
      else upsertStudentRecord(updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      showSnack("Image uploaded");
    } catch (err) {
      showSnack(err?.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploadingImage(false);
      if (event.target) event.target.value = "";
    }
  };

  const openImagesStore = async () => {
    const { schooleId } = getUserContext();
    if (!schooleId || !classId) return;
    setImagesStore((p) => ({ ...p, open: true, loading: true, rows: [], selectedUrls: [] }));
    try {
      const body = { schooleId, classId };
      if (sectionId) body.sectionId = sectionId;
      const rows = await ImagesStoreService.findImagesStoreList(body);
      setImagesStore((p) => ({
        ...p,
        loading: false,
        rows: Array.isArray(rows) ? rows : [],
      }));
    } catch {
      setImagesStore((p) => ({ ...p, loading: false, rows: [] }));
      showSnack("Failed to load Images Store");
    }
  };

  const saveImagesFromStore = async () => {
    if (!detailContext || !imagesStore.selectedUrls.length) return;
    setUploadingImage(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      const existing = Array.isArray(record.imageURL) ? record.imageURL.filter(Boolean) : [];
      const remaining = MAX_IMAGES - existing.length;
      const newUrls = imagesStore.selectedUrls
        .filter((u) => !existing.includes(u))
        .slice(0, remaining);
      if (!newUrls.length) return;
      const imageURL = [...existing, ...newUrls];
      await ActivityService.patchActivityRecord({ _id: record._id, imageURL });
      const updated = { ...record, imageURL };
      if (sideTab === "works") upsertWorkRecord(detailContext.targetStudentId, updated);
      else upsertStudentRecord(updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      setImagesStore((p) => ({ ...p, open: false, selectedUrls: [] }));
      showSnack("Images added from store");
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to add images");
    } finally {
      setUploadingImage(false);
    }
  };

  const openHistory = (record) => {
    setHistoryRows(Array.isArray(record?.statusHistory) ? record.statusHistory : []);
    setHistoryOpen(true);
  };

  const matchesCenterSearch = (text) => {
    const q = centerSearch.trim().toLowerCase();
    if (!q) return true;
    return (text || "").toLowerCase().includes(q);
  };

  const handleClassChange = (value) => {
    const nextClassId = String(value || "");
    setClassId(nextClassId);
    setSectionId("");
    setStudentId("");
    setActivityId("");
    setSuperActivityId("");
    setSelectedExercise(null);
    loadSections(nextClassId);
    loadAreasAndActivities(nextClassId, true);
  };

  const handleSectionChange = (value) => {
    const nextSectionId = value ? String(value) : "";
    setSectionId(nextSectionId);
    setStudentId("");
  };

  const closeRowMenu = () => {
    setRowMenu({
      anchor: null,
      activity: null,
      subActivity: null,
      targetStudentId: null,
      record: null,
    });
  };

  const openRowMenu = (event, activity, subActivity, targetStudentId, record) => {
    event.stopPropagation();
    setRowMenu({
      anchor: event.currentTarget,
      activity,
      subActivity,
      targetStudentId,
      record,
    });
  };

  const renderStudentModeExerciseRow = (activity, subActivity, index) => {
    const label = subActivity?.subActivityName || activity.activityName;
    const description =
      subActivity?.description ||
      subActivity?.remarks ||
      activity.description ||
      selectedActivity?.activityName ||
      "Track progress for this exercise.";
    const record = getStudentRecord(activity._id, subActivity?._id);
    const rowKey = recordMapKey(activity._id, subActivity?._id);
    const iconMeta = getDefaultExerciseIcon(index);
    const IconComp = iconMeta.Icon;

    return (
      <Box key={rowKey} className={classes.exerciseCard}>
        <Box
          className={classes.exerciseIconBox}
          style={{ backgroundColor: iconMeta.bg, color: iconMeta.color }}
        >
          <IconComp style={{ fontSize: 24 }} />
        </Box>
        <Box className={classes.exerciseInfo}>
          <Typography className={classes.exerciseTitle}>{label}</Typography>
          <Typography className={classes.exerciseDesc}>{description}</Typography>
        </Box>
        <Box className={classes.exerciseProgress}>
          <ActivityKeyStepper
            currentKey={record?.key}
            updating={updatingKey === rowKey}
            compact
            onSelect={(key) => requestKeyUpdate(activity, subActivity, key)}
          />
        </Box>
        <Box className={classes.exerciseActions}>
          <Tooltip title="Add observation">
            <IconButton
              size="small"
              className={classes.addRecordBtn}
              onClick={() => openDetail(activity, subActivity)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) => openRowMenu(e, activity, subActivity, studentId, record)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderWorksModeStudentRow = (student, index) => {
    const record = getWorkRecord(student._id);
    const rowKey = studentRecordKey(
      student._id,
      selectedExercise.activity._id,
      selectedExercise.subActivity?._id
    );
    const avatarStyle = avatarColorsFromName(student.name);

    return (
      <Box key={student._id} className={classes.exerciseCard}>
        <Avatar className={classes.sideAvatar} style={avatarStyle}>
          {studentInitialsFromName(student.name)}
        </Avatar>
        <Box className={classes.exerciseInfo}>
          <Typography className={classes.exerciseTitle} style={{ textTransform: "uppercase" }}>
            {student.name}
            {student.ageLabel && (
              <span style={{ color: "#9aa6b6", fontWeight: 500, textTransform: "none" }}>
                {" "}({student.ageLabel})
              </span>
            )}
          </Typography>
          <Typography className={classes.exerciseDesc}>
            Updated {formatUpdatedDate(record?.updatedAt)}
            {record?._id ? ` · ${isRecordPublished(record) ? "Published" : "Draft"}` : ""}
          </Typography>
        </Box>
        <Box className={classes.exerciseProgress}>
          <ActivityKeyStepper
            currentKey={record?.key}
            updating={updatingKey === rowKey}
            compact
            onSelect={(key) =>
              requestKeyUpdate(
                selectedExercise.activity,
                selectedExercise.subActivity,
                key,
                student._id
              )
            }
          />
        </Box>
        <Box className={classes.exerciseActions}>
          <Tooltip title="Add observation">
            <IconButton
              size="small"
              className={classes.addRecordBtn}
              onClick={() =>
                openDetail(
                  selectedExercise.activity,
                  selectedExercise.subActivity,
                  student._id
                )
              }
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={(e) =>
              openRowMenu(
                e,
                selectedExercise.activity,
                selectedExercise.subActivity,
                student._id,
                record
              )
            }
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const handleAreaChange = (value) => {
    setSuperActivityId(String(value || ""));
    setActivityId("");
    setSelectedExercise(null);
  };

  const handleActivityChange = (value) => {
    const nextActivityId = String(value || "");
    setActivityId(nextActivityId);
    setSelectedExercise(null);
    const activity = classActivities.find((a) => String(a._id) === nextActivityId);
    if (activity) {
      const nextSuperId = resolveSuperActivityId(activity.superActivityId);
      if (nextSuperId && nextSuperId !== normalizeId(superActivityId)) {
        setSuperActivityId(nextSuperId);
      }
    }
  };

  const renderStudentModeCenter = () => {
    if (!activityId) {
      return (
        <Typography className={classes.emptyState}>
          Select area of work and list of activity.
        </Typography>
      );
    }

    const visible = flatExercises.filter((ex) => matchesCenterSearch(ex.label));
    if (!visible.length) {
      return (
        <Typography className={classes.emptyState}>
          No exercises found for this activity.
        </Typography>
      );
    }

    return visible.map((ex, idx) =>
      renderStudentModeExerciseRow(ex.activity, ex.subActivity, idx)
    );
  };

  const renderWorksCenter = () => {
    if (!selectedExercise) {
      return (
        <Typography className={classes.emptyState}>
          Select an exercise from the left list.
        </Typography>
      );
    }

    const filteredStudents = studentList.filter((s) => matchesCenterSearch(s.name));

    if (!filteredStudents.length) {
      return (
        <Typography className={classes.emptyState} style={{ padding: 24 }}>
          No students found.
        </Typography>
      );
    }

    return filteredStudents.map((student, idx) => renderWorksModeStudentRow(student, idx));
  };

  const detailImages =
    detailContext?.record?.imageURL && Array.isArray(detailContext.record.imageURL)
      ? detailContext.record.imageURL.filter(Boolean)
      : [];

  const storeVisibleImages = imagesStore.rows.flatMap((row) =>
    (row.imageURL || [])
      .map((url) => ({ url, eventName: row.eventName || "—" }))
      .filter((item) => item.url)
  );

  const detailPanel = detailContext && (
    <Box className={classes.detailContent}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
            {detailContext.activity?.activityName}
          </Typography>
          {detailContext.subActivity?.subActivityName && (
            <Typography style={{ fontSize: "0.85rem", color: "#9aa6b6" }}>
              {detailContext.subActivity.subActivityName}
            </Typography>
          )}
          {sideTab === "works" && (
            <Typography style={{ fontSize: "0.8rem", color: "#1769d6", marginTop: 4 }}>
              {getStudentNameWithAgeById(detailContext.targetStudentId)}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={closeDetail}>
          <CloseIcon />
        </IconButton>
      </Box>

      <ActivityKeyStepper
        currentKey={detailContext.record?.key}
        updating={
          updatingKey ===
          (sideTab === "works"
            ? studentRecordKey(
                detailContext.targetStudentId,
                detailContext.activity._id,
                detailContext.subActivity?._id
              )
            : recordMapKey(detailContext.activity._id, detailContext.subActivity?._id))
        }
        onSelect={(key) =>
          requestKeyUpdate(
            detailContext.activity,
            detailContext.subActivity,
            key,
            detailContext.targetStudentId
          )
        }
      />

      <Box>
        <Typography className={classes.topLabel}>Publish Status</Typography>
        <Box mt={0.5}>
          {detailContext.record?._id ? (
            renderPublishStatusChip(detailContext.record)
          ) : (
            <Typography style={{ fontSize: "0.85rem", color: "#9aa6b6" }}>
              Save a record first to publish
            </Typography>
          )}
        </Box>
      </Box>

      <TextField
        label="Observation"
        placeholder={
          isListeningObservation
            ? "Listening… speak now"
            : "Add observation notes… or tap mic to speak"
        }
        multiline
        rows={5}
        variant="outlined"
        fullWidth
        value={observationText}
        onChange={(e) => setObservationText(e.target.value)}
        helperText={
          isListeningObservation
            ? "Voice to text is on. Tap the mic again to stop."
            : "Tap the mic to dictate into this box (Chrome / Edge)."
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" style={{ alignSelf: "flex-start", marginTop: 8 }}>
              <Tooltip
                title={
                  isListeningObservation
                    ? "Stop voice input"
                    : "Start voice to text"
                }
              >
                <IconButton
                  aria-label={
                    isListeningObservation
                      ? "Stop voice input"
                      : "Start voice to text"
                  }
                  onClick={toggleObservationVoice}
                  size="small"
                  style={{
                    color: isListeningObservation ? "#c62828" : "#1769d6",
                    backgroundColor: isListeningObservation
                      ? "#ffebee"
                      : "#e8f0fe",
                  }}
                >
                  {isListeningObservation ? (
                    <MicOffIcon fontSize="small" />
                  ) : (
                    <MicIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />

      <Box>
        <Typography className={classes.topLabel}>Images</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 8 }}>
          {detailImages.map((url) => (
            <img key={url} src={url} alt="" className={classes.imageThumb} />
          ))}
          {detailImages.length < MAX_IMAGES && (
            <>
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={uploadingImage ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                disabled={uploadingImage}
              >
                Upload
                <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhotoLibraryIcon />}
                onClick={openImagesStore}
                disabled={uploadingImage}
              >
                From Store
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Box mt="auto" display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button onClick={closeDetail}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveObservation}
          disabled={savingDetail}
          style={{ background: "#1769d6" }}
        >
          {savingDetail ? "Saving..." : "Save Observation"}
        </Button>
      </Box>
    </Box>
  );

  const selectedAreaName =
    filteredAreas.find((a) => String(a._id) === String(superActivityId))?.superActivityName || "";

  const renderSidePanelContent = () => (
    <>
      <Tabs
        value={sideTab}
        onChange={(_, v) => {
          setSideTab(v);
          setSideSearch("");
        }}
        indicatorColor="primary"
        textColor="primary"
        className={classes.sideTabs}
        variant="fullWidth"
      >
        <Tab
          label={
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <GroupIcon fontSize="small" />
              Students
            </Box>
          }
          value="students"
        />
        <Tab
          label={
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <WorkOutlineIcon fontSize="small" />
              Works
            </Box>
          }
          value="works"
        />
      </Tabs>
      <Box className={classes.sideSearchRow}>
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          className={classes.sideSearch}
          placeholder={sideTab === "students" ? "Search students..." : "Search exercises..."}
          value={sideSearch}
          onChange={(e) => setSideSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#9aa6b6" }} />
              </InputAdornment>
            ),
          }}
        />
        <IconButton size="small" className={classes.sideFilterBtn}>
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.sideList}>
        {sideTab === "students" && (
          <>
            {!loadingStudents && studentList.length > 0 && (
              <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                style={{ gap: 6, padding: "10px 12px", borderBottom: "1px solid #f4f6fa" }}
              >
                <Typography
                  variant="caption"
                  style={{
                    width: "100%",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#46566b",
                    marginBottom: 2,
                  }}
                >
                  Age Group
                </Typography>
                <Chip
                  size="small"
                  label={`All (${studentList.length})`}
                  clickable
                  onClick={handleSelectAllAgeChip}
                  style={{
                    backgroundColor: !selectedAgeFilter ? "#1769d6" : "#e8f0fe",
                    color: !selectedAgeFilter ? "#fff" : "#1769d6",
                    fontWeight: 700,
                  }}
                />
                {studentAgeWiseSummary.map(([label, count]) => {
                  const isActive = selectedAgeFilter === label;
                  return (
                    <Chip
                      key={label}
                      size="small"
                      clickable
                      label={`${label} (${count})`}
                      onClick={() => handleAgeChipSelect(label)}
                      variant={isActive ? "default" : "outlined"}
                      style={
                        isActive
                          ? { backgroundColor: "#1769d6", color: "#fff", fontWeight: 600 }
                          : undefined
                      }
                    />
                  );
                })}
                {selectedAgeFilter && (
                  <Typography
                    variant="caption"
                    style={{ width: "100%", color: "#9aa6b6", marginTop: 2 }}
                  >
                    Showing {filteredSideStudents.length} student(s) in “{selectedAgeFilter}”
                  </Typography>
                )}
              </Box>
            )}
            {loadingStudents && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={22} />
              </Box>
            )}
            {filteredSideStudents.map((student) => {
              const avatarStyle = avatarColorsFromName(student.name);
              return (
                <Box
                  key={student._id}
                  className={`${classes.sideItem} ${
                    studentId === student._id ? classes.sideItemActive : ""
                  }`}
                  onClick={() => {
                    setStudentId(student._id);
                    if (isMobile) setMobileStudentDrawerOpen(false);
                  }}
                >
                  <Avatar className={classes.sideAvatar} style={avatarStyle}>
                    {studentInitialsFromName(student.name)}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography className={classes.sideItemName}>{student.name}</Typography>
                    {student.ageLabel && (
                      <Typography className={classes.sideItemSub}>{student.ageLabel}</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {!loadingStudents && !filteredSideStudents.length && (
              <Typography className={classes.emptyState} style={{ padding: 16 }}>
                No students
              </Typography>
            )}
          </>
        )}
        {sideTab === "works" && (
          <>
            {loadingActivities && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={22} />
              </Box>
            )}
            {filteredSideExercises.map((ex, idx) => {
              const iconMeta = getDefaultExerciseIcon(idx);
              const SideIcon = iconMeta.Icon;
              return (
                <Box
                  key={ex.id}
                  className={`${classes.sideItem} ${
                    selectedExercise?.id === ex.id ? classes.sideItemActive : ""
                  }`}
                  onClick={() => {
                    setSelectedExercise(ex);
                    if (isMobile) setMobileStudentDrawerOpen(false);
                  }}
                >
                  <Box
                    className={classes.exerciseIconBox}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      backgroundColor: iconMeta.bg,
                      color: iconMeta.color,
                    }}
                  >
                    <SideIcon style={{ fontSize: 18 }} />
                  </Box>
                  <Box minWidth={0}>
                    <Typography className={classes.sideItemName} style={{ textTransform: "none" }}>
                      {ex.label}
                    </Typography>
                    {ex.parentLabel && (
                      <Typography className={classes.sideItemSub}>{ex.parentLabel}</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {!loadingActivities && !filteredSideExercises.length && (
              <Typography className={classes.emptyState} style={{ padding: 16 }}>
                {activityId ? "No exercises for this activity" : "Select list of activity"}
              </Typography>
            )}
          </>
        )}
      </Box>
    </>
  );

  const classFilterOptions = useMemo(
    () => classList.map((c) => ({ value: String(c._id), label: c.className })),
    [classList]
  );

  const sectionFilterOptions = useMemo(
    () => [
      { value: "", label: "All sections" },
      ...sectionList.map((s) => ({ value: String(s._id), label: s.sectionName })),
    ],
    [sectionList]
  );

  const areaFilterOptions = useMemo(
    () =>
      filteredAreas.map((a) => ({
        value: String(a._id),
        label: a.superActivityName,
      })),
    [filteredAreas]
  );

  const activityFilterOptions = useMemo(
    () =>
      (superActivityId ? areaActivities : classActivities).map((a) => ({
        value: String(a._id),
        label: a.activityName,
      })),
    [areaActivities, classActivities, superActivityId]
  );

  const getFilterLabel = (options, value, emptyLabel) => {
    const normalized = value ? String(value) : "";
    const match = options.find((opt) => String(opt.value) === normalized);
    return match?.label || emptyLabel;
  };

  const renderFilterField = (label, icon, value, onChange, options, config = {}) => {
    const normalizedValue = value ? String(value) : "";
    const emptyLabel = config.emptyLabel || "Select...";

    return (
      <Box minWidth={0}>
        <Typography className={classes.topLabel}>{label}</Typography>
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.filterFormControl}
          disabled={config.disabled}
        >
          <Select
            value={normalizedValue}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
            input={
              <OutlinedInput
                startAdornment={
                  <InputAdornment position="start" className={classes.filterSelectIconWrap}>
                    {icon}
                  </InputAdornment>
                }
                notched={false}
              />
            }
            renderValue={(selected) => (
              <Typography component="span" className={classes.filterSelectValue}>
                {getFilterLabel(options, selected, emptyLabel)}
              </Typography>
            )}
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              getContentAnchorEl: null,
            }}
          >
            {options.map((opt) => (
              <MenuItem
                key={opt.value === "" ? "__empty__" : String(opt.value)}
                value={String(opt.value)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box>
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>Quick activity allotment</Typography>
        <Button
          variant="outlined"
          className={classes.detailsViewBtn}
          startIcon={<ViewListIcon />}
          onClick={() => history.push("/app/studentactivity")}
        >
          Details View
        </Button>
      </Box>

      <Box className={classes.root}>
        <Box className={classes.topBar}>
          {renderFilterField(
            "Classroom",
            <SchoolIcon />,
            classId,
            handleClassChange,
            classFilterOptions,
            { emptyLabel: "Select class" }
          )}
          {renderFilterField(
            "Section",
            <GroupIcon />,
            sectionId,
            handleSectionChange,
            sectionFilterOptions,
            {
              emptyLabel: "All sections",
              disabled: !classId || sectionFilterOptions.length <= 1,
            }
          )}
          {renderFilterField(
            "Area of Work",
            <WorkOutlineIcon />,
            superActivityId,
            handleAreaChange,
            areaFilterOptions,
            { emptyLabel: "Select area" }
          )}
          {renderFilterField(
            "List of Activity",
            <AssignmentIcon />,
            activityId,
            handleActivityChange,
            activityFilterOptions,
            { emptyLabel: "Select activity", disabled: !classActivities.length }
          )}
          <IconButton onClick={refreshRecords} title="Refresh" className={classes.refreshBtn}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {isMobile && (
          <Box
            className={classes.mobileStudentBar}
            onClick={() => setMobileStudentDrawerOpen(true)}
          >
            <Avatar
              className={classes.sideAvatar}
              style={avatarColorsFromName(selectedStudent?.name || "")}
            >
              {studentInitialsFromName(selectedStudent?.name || "?")}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography className={classes.mainStudentName} style={{ fontSize: "0.95rem" }}>
                {sideTab === "students"
                  ? selectedStudent?.name || "Select student"
                  : selectedExercise?.label || "Select exercise"}
                {sideTab === "students" && selectedStudent?.ageLabel && (
                  <span style={{ color: "#9aa6b6", fontWeight: 500, fontSize: "0.8rem" }}>
                    {" "}({selectedStudent.ageLabel})
                  </span>
                )}
              </Typography>
              <Typography style={{ fontSize: "0.75rem", color: "#9aa6b6" }}>
                Tap to change {sideTab === "students" ? "student" : "exercise"}
              </Typography>
            </Box>
            <ChevronRightIcon style={{ color: "#9aa6b6" }} />
          </Box>
        )}

        <Box className={classes.body}>
          <Card className={`${classes.sidePanel} ${isMobile ? classes.sidePanelHidden : ""}`} elevation={0}>
            {renderSidePanelContent()}
          </Card>

          <Card className={classes.mainPanel} elevation={0}>
            <Box className={classes.mainHeader}>
              <Box className={classes.mainHeaderLeft}>
                {sideTab === "students" ? (
                  <>
                    <Avatar
                      className={classes.mainAvatar}
                      style={avatarColorsFromName(selectedStudent?.name || "")}
                    >
                      {studentInitialsFromName(selectedStudent?.name || "?")}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography className={classes.mainStudentName}>
                        {selectedStudent?.name || "Select a student"}
                        {selectedStudent?.ageLabel && (
                          <span style={{ color: "#9aa6b6", fontWeight: 500, fontSize: "0.85rem" }}>
                            {" "}({selectedStudent.ageLabel})
                          </span>
                        )}
                      </Typography>
                      <Typography className={classes.breadcrumb}>
                        <strong>{selectedActivity?.activityName || "Select activity"}</strong>
                        {selectedAreaName ? ` › ${selectedAreaName}` : ""}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  (() => {
                    const worksIcon = getDefaultExerciseIcon(0);
                    const WorksIcon = worksIcon.Icon;
                    return (
                      <>
                        <Box
                          className={classes.exerciseIconBox}
                          style={{
                            width: 52,
                            height: 52,
                            backgroundColor: worksIcon.bg,
                            color: worksIcon.color,
                          }}
                        >
                          <WorksIcon style={{ fontSize: 26 }} />
                        </Box>
                        <Box minWidth={0}>
                          <Typography className={classes.mainStudentName}>
                            {selectedExercise?.label || "Select an exercise"}
                          </Typography>
                          <Typography className={classes.breadcrumb}>
                            <strong>{selectedAreaName || "Select area of work"}</strong>
                          </Typography>
                        </Box>
                      </>
                    );
                  })()
                )}
              </Box>
              <Box className={classes.mainHeaderActions}>
                <TextField
                  size="small"
                  variant="outlined"
                  className={classes.centerSearch}
                  placeholder={sideTab === "students" ? "Search exercise..." : "Search student..."}
                  value={centerSearch}
                  onChange={(e) => setCenterSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" style={{ color: "#9aa6b6" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton size="small" className={classes.sideFilterBtn}>
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box className={classes.activityList}>
              {loadingRecords && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!loadingRecords && sideTab === "students" && activityId &&
                renderStudentModeCenter()}
              {!loadingRecords && sideTab === "students" && !activityId && (
                <Typography className={classes.emptyState}>
                  Select area of work and list of activity.
                </Typography>
              )}
              {!loadingRecords && sideTab === "works" && renderWorksCenter()}
            </Box>
          </Card>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={mobileStudentDrawerOpen}
        onClose={() => setMobileStudentDrawerOpen(false)}
        classes={{ paper: classes.studentDrawerPaper }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
          <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
            {sideTab === "students" ? "Students" : "Works"}
          </Typography>
          <IconButton size="small" onClick={() => setMobileStudentDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {renderSidePanelContent()}
      </Drawer>

      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={closeRowMenu}
        keepMounted
      >
        <MenuItem
          onClick={() => {
            openDetail(rowMenu.activity, rowMenu.subActivity, rowMenu.targetStudentId);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <NoteAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add observation" />
        </MenuItem>
        {rowMenu.record?._id && (
          <MenuItem
            onClick={() => {
              openHistory(rowMenu.record);
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Status history" />
          </MenuItem>
        )}
        {rowMenu.record?._id && (
          <MenuItem
            onClick={() => {
              handlePublicStatusChange(
                rowMenu.record,
                isRecordPublished(rowMenu.record) ? "draft" : "publish"
              );
              closeRowMenu();
            }}
          >
            <ListItemIcon>
              <PublishIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={isRecordPublished(rowMenu.record) ? "Move to draft" : "Publish to parents"}
            />
          </MenuItem>
        )}
      </Menu>

      {isMobile ? (
        <Dialog fullScreen open={detailOpen} onClose={closeDetail}>
          <DialogTitle disableTypography>
            <Box display="flex" alignItems="center">
              <IconButton edge="start" onClick={closeDetail}><ArrowBackIcon /></IconButton>
              <Typography style={{ fontWeight: 700, marginLeft: 8 }}>Observation & Images</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>{detailPanel}</DialogContent>
        </Dialog>
      ) : (
        <Drawer anchor="right" open={detailOpen} onClose={closeDetail}>
          <Box className={classes.detailDrawer}>{detailPanel}</Box>
        </Drawer>
      )}

      <Dialog
        open={addRecordDialog.open}
        onClose={addRecordDialog.saving ? undefined : closeAddRecordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Activity Record</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Box>
              <Typography className={classes.topLabel}>Student</Typography>
              <Typography style={{ fontWeight: 600, color: "#0f2d4d" }}>
                {getStudentNameWithAgeById(addRecordDialog.targetStudentId)}
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.topLabel}>Area of Work</Typography>
              <Typography style={{ color: "#46566b" }}>
                {filteredAreas.find((a) => String(a._id) === String(superActivityId))?.superActivityName || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.topLabel}>List of Activity</Typography>
              <Typography style={{ color: "#46566b" }}>
                {addRecordDialog.activity?.activityName || selectedActivity?.activityName || "—"}
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.topLabel}>Exercise</Typography>
              <Typography style={{ color: "#46566b" }}>
                {addRecordDialog.subActivity?.subActivityName ||
                  addRecordDialog.activity?.activityName ||
                  "—"}
              </Typography>
            </Box>
            <Box>
              <Typography className={classes.topLabel}>Key</Typography>
              <Typography style={{ fontWeight: 600, color: "#1769d6" }}>
                {getKeyStatusText(addRecordDialog.key)}
              </Typography>
            </Box>
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={addRecordDialog.startDate}
              onChange={(e) =>
                setAddRecordDialog((p) => ({ ...p, startDate: e.target.value }))
              }
            />
            <TextField
              label="End Date"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={addRecordDialog.endDate}
              onChange={(e) =>
                setAddRecordDialog((p) => ({ ...p, endDate: e.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddRecordDialog} disabled={addRecordDialog.saving}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmAddRecord}
            disabled={addRecordDialog.saving}
            style={{ background: "#1769d6" }}
          >
            {addRecordDialog.saving ? "Saving..." : "Save Record"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={keyConfirm.open} onClose={() => setKeyConfirm((p) => ({ ...p, open: false }))}>
        <DialogTitle>Update Progress</DialogTitle>
        <DialogContent>
          <Typography>
            Change status from{" "}
            <strong>{getKeyStatusText(keyConfirm.currentKey)}</strong> to{" "}
            <strong>{getKeyStatusText(keyConfirm.nextKey)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyConfirm((p) => ({ ...p, open: false }))}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={confirmKeyUpdate} style={{ background: "#1769d6" }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusConfirmDialog.open} onClose={closeStatusConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {statusConfirmDialog.publicStatus === "publish" ? "Publish activity?" : "Move to draft?"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ color: "#46566b", marginBottom: 12 }}>
            <strong style={{ color: "#e65100" }}>Draft</strong> — visible to teachers only, not sent to parents.
          </Typography>
          <Typography variant="body2" style={{ color: "#46566b", marginBottom: 12 }}>
            <strong style={{ color: "#1a9b54" }}>Published</strong> — sent to parents.
          </Typography>
          <Typography variant="body2">
            {statusConfirmDialog.publicStatus === "publish"
              ? "Publish this activity record to parents?"
              : "Move this activity record back to draft?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusConfirmDialog}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmPublicStatusChange}
            style={{ background: "#1769d6" }}
          >
            {statusConfirmDialog.publicStatus === "publish" ? "Publish" : "Move to Draft"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Status History</DialogTitle>
        <DialogContent dividers>
          {!historyRows.length && (
            <Typography color="textSecondary">No history recorded.</Typography>
          )}
          {historyRows.map((row, idx) => (
            <Box key={idx} mb={1}>
              <Typography style={{ fontWeight: 600 }}>
                {getKeyStatusText(row.key)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatUpdatedDate(row.changedAt)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imagesStore.open} onClose={() => setImagesStore((p) => ({ ...p, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Images Store</DialogTitle>
        <DialogContent dividers>
          {imagesStore.loading && <CircularProgress size={24} />}
          {!imagesStore.loading && !storeVisibleImages.length && (
            <Typography color="textSecondary">No images in store.</Typography>
          )}
          <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {storeVisibleImages.map((item) => {
              const selected = imagesStore.selectedUrls.includes(item.url);
              return (
                <Tooltip key={item.url} title={item.eventName}>
                  <img
                    src={item.url}
                    alt=""
                    className={`${classes.storeImage} ${selected ? classes.storeImageSelected : ""}`}
                    onClick={() =>
                      setImagesStore((p) => ({
                        ...p,
                        selectedUrls: selected
                          ? p.selectedUrls.filter((u) => u !== item.url)
                          : [...p.selectedUrls, item.url],
                      }))
                    }
                  />
                </Tooltip>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagesStore((p) => ({ ...p, open: false }))}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!imagesStore.selectedUrls.length || uploadingImage}
            onClick={saveImagesFromStore}
            style={{ background: "#1769d6" }}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
}
